import {Injectable, signal} from '@angular/core';
import {Observable, Subject, of} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {DARK_THEME} from '../styles/theme.dark';
import {DEFAULT_THEME} from '../styles/theme.default';
import {APP_DARK_CLASS, AppPreset} from './app.preset';

export type AppTheme = 'default' | 'dark';

@Injectable({providedIn: 'root'})
export class AppThemeService {
	private readonly themeSignal = signal<AppTheme>(this.resolveInitialTheme());
	private readonly themeChange$ = new Subject<{name: AppTheme}>();

	constructor() {
		this.apply(this.themeSignal());
	}

	get currentTheme(): AppTheme {
		return this.themeSignal();
	}

	onThemeChange(): Observable<{name: AppTheme}> {
		return this.themeChange$.asObservable();
	}

	changeTheme(name: AppTheme | string): void {
		const theme = name === 'dark' ? 'dark' : 'default';
		localStorage.setItem('theme', theme);
		this.themeSignal.set(theme);
		this.apply(theme);
		this.themeChange$.next({name: theme});
	}

	toggleTheme(): void {
		this.changeTheme(this.themeSignal() === 'dark' ? 'default' : 'dark');
	}

	isDark(): boolean {
		return this.themeSignal() === 'dark';
	}

	getJsTheme(): Observable<typeof DEFAULT_THEME> {
		return this.onThemeChange().pipe(
			startWith({name: this.themeSignal()}),
			map(({name}) => (name === 'dark' ? DARK_THEME : DEFAULT_THEME) as typeof DEFAULT_THEME),
		);
	}

	onMediaQueryChange(): Observable<[unknown, {width: number}]> {
		return of([null, {width: window.innerWidth}]);
	}

	private resolveInitialTheme(): AppTheme {
		const stored = localStorage.getItem('theme');
		if (stored === 'dark' || stored === 'default') {
			return stored;
		}
		const prefersDark =
			window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
		return prefersDark ? 'dark' : 'default';
	}

	private apply(theme: AppTheme): void {
		const root = document.documentElement;
		const body = document.body;
		root.classList.toggle(APP_DARK_CLASS, theme === 'dark');
		body.classList.remove('app-theme-default', 'app-theme-dark', 'nb-theme-default', 'nb-theme-dark', 'nb-theme-cosmic', 'nb-theme-corporate');
		body.classList.add(theme === 'dark' ? 'app-theme-dark' : 'app-theme-default');
	}
}

export {AppPreset};
